import React from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import ApplianceConditionRulesContainer from '../rules/ApplianceConditionRulesContainer';
import AutoCompleteFilter from 'components/list/Filters/types/AutoCompleteFilter';
import MobianDateTimeInput from 'components/input/MobianDateTimeInput';
import RenderBooleanInputField from 'components/form/InputFields/RenderBooleanInputField';
import RenderPercentageInputField from 'components/form/InputFields/RenderPercentageInputField';
import RenderNullableAutoCompleteInputField from 'components/form/InputFields/RenderNullableAutoCompleteInputField';
import RenderTextInputField from 'components/form/InputFields/RenderTextInputField';
import SubmitButton from 'components/button/SubmitButton';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import authHelper from 'helpers/auth';
import validations from 'helpers/forms/validations';
import { ButtonWrapper, FormWrapper, Item, Row, Section } from 'components/ui/table/elements';
import { Field, getFormValues, isInvalid, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

class DiscountsEditForm extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.currencies = context.config.currency.supported;

        const {
            appliance_rules,
            conditions_rules,
        } = props.data;

        this.state = {
            rules: {
                appliance: appliance_rules || null,
                condition: conditions_rules || null,
            },
        };
    }

    componentDidMount() {
        const {
            data,
            initialize,
        } = this.props;

        data.percentage = data.percentage && data.percentage * 100;

        initialize(data);
    }

    componentDidUpdate(prevProps) {
        const { formValues } = this.props;

        if (!utils.isEmpty(utils.diffObjects(formValues, prevProps.formValues))) {
            return this.setState({
                rules: {
                    appliance: formValues.appliance_rules || null,
                    condition: formValues.conditions_rules || null,
                },
            });
        }
    }

    onUpdateRules = (rules) => {
        const { change } = this.props;

        this.setState({
            rules: {
                appliance: rules.appliance_rules,
                condition: rules.conditions_rules,
            },
        }, () => {
            utils.each(rules, (rule, key) => {
                change(key, rule);
            });
        });

    };

    render() {
        const {
            data,
            formValues,
            handleSubmit,
            handleSubmitForm,
            invalid,
            submitting,
            translate,
        } = this.props;

        const { rules } = this.state;

        const currencies = utils.map(this.currencies, (currency) => {
            return {
                label: currency,
                value: currency,
            };
        });
        const isAdmin = authHelper.isAdmin();

        const isGlobalDiscount = !utils.isEmpty(formValues) && (!formValues.uses_discount_codes);

        const booleanInputLabelStyle = {
            color: 'rgba(0, 0, 0, 0.298039)',
        };

        return (
            <FormWrapper>
                <form onSubmit={ handleSubmit(handleSubmitForm) }>
                    <Section>
                        {isAdmin &&
                            <Row>
                                <Item>
                                    <Field
                                        component={ AutoCompleteFilter }
                                        label='resources.discounts.fields.accountId'
                                        name='account_id'
                                        optionValue='id'
                                        reference='accounts'
                                        searchBy='name'
                                        searchParams={ {
                                            size: '12',
                                            sort_by: 'name',
                                            sort_direction: 'asc',
                                        } }
                                        source='account_id'
                                    />
                                </Item>
                            </Row>
                        }
                        <Row>
                            <Item>
                                <Field
                                    component={ RenderBooleanInputField }
                                    fullWidth={ true }
                                    label={ translate('resources.discounts.fields.is_enabled') }
                                    labelStyle={ booleanInputLabelStyle }
                                    resource='is_enabled'
                                    name='is_enabled'
                                />
                            </Item>
                        </Row>

                        <Row>
                            <Item>
                                <Field
                                    component={ RenderBooleanInputField }
                                    fullWidth={ true }
                                    label={ translate('resources.discounts.fields.uses_discount_codes') }
                                    labelStyle={ booleanInputLabelStyle }
                                    startingValue={ data.uses_discount_codes }
                                    resource='uses_discount_codes'
                                    name='uses_discount_codes'
                                />
                            </Item>
                        </Row>

                        <Row>
                            <Item>
                                <Field
                                    component={ RenderBooleanInputField }
                                    fullWidth={ false }
                                    label={ translate('resources.discounts.fields.apply_on_fees') }
                                    name='apply_on_fees'
                                />
                            </Item>
                        </Row>

                        <Row>
                            <Item>
                                <Field
                                    component={ RenderTextInputField }
                                    fullWidth={ true }
                                    label={ translate('resources.discounts.fields.title') }
                                    name='title'
                                    validate={ validations.required }
                                />
                            </Item>
                        </Row>
                    </Section>

                    {isGlobalDiscount &&
                        <div>
                            <Section>
                                <Row>
                                    <Item>
                                        <Field
                                            component={ RenderTextInputField }
                                            fullWidth={ true }
                                            label={ translate('resources.discounts.fields.priority') }
                                            name='priority'
                                            resource='priority'
                                            validate={ validations.number }
                                        />
                                    </Item>
                                </Row>

                                <Row>
                                    <Item>
                                        <Field
                                            component={ MobianDateTimeInput }
                                            floatingLabelFixed={ true }
                                            fullWidth={ true }
                                            label={ translate('resources.discounts.fields.expiry_date') }
                                            name='expiry_date'
                                            resource='expiry_date'
                                        />
                                    </Item>
                                </Row>
                            </Section>

                            <Section>
                                <Row>
                                    <Item>
                                        <Field
                                            choices={ currencies }
                                            component={ RenderNullableAutoCompleteInputField }
                                            fullWidth={ false }
                                            label={ translate('resources.discounts.fields.currency') }
                                            name='currency'
                                            optionText='label'
                                            optionValue='value'
                                            startingValue={ data.currency || null }
                                        />
                                    </Item>
                                </Row>

                                <Row>
                                    <Item>
                                        <Field
                                            component={ RenderTextInputField }
                                            fullWidth={ true }
                                            label={ translate('resources.discounts.fields.amount') }
                                            name='amount'
                                            validate={ validations.number }
                                        />
                                    </Item>
                                </Row>

                                <Row>
                                    <Item>
                                        <Field
                                            component={ RenderPercentageInputField }
                                            fullWidth={ true }
                                            label={ translate('resources.discounts.fields.percentage') }
                                            name='percentage'
                                        />
                                    </Item>
                                </Row>
                            </Section>
                        </div>
                    }

                    <Section>
                        <ApplianceConditionRulesContainer discountRules={ rules } updateRules={ this.onUpdateRules } />
                    </Section>

                    <Section>
                        <Row>
                            <ButtonWrapper>
                                <SubmitButton
                                    disabled={ submitting || invalid }
                                    loading={ submitting }
                                />
                            </ButtonWrapper>
                        </Row>
                    </Section>
                </form>
            </FormWrapper>
        );
    }
}

DiscountsEditForm.contextTypes = {
    config: PropTypes.object,
};

DiscountsEditForm.propTypes = {
    change: PropTypes.func.isRequired,
    data: PropTypes.object,
    formValues: PropTypes.shape({
        uses_discount_codes: PropTypes.bool,
        appliance_rules: PropTypes.arrayOf(PropTypes.object),
        conditions_rules: PropTypes.arrayOf(PropTypes.object),
    }),
    handleSubmit: PropTypes.func.isRequired,
    handleSubmitForm: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    invalid: PropTypes.bool,
    submitting: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => {
    const {
        form,
    } = props;

    return {
        invalid: isInvalid(form)(state),
        formValues: getFormValues(form)(state),
    };
};

const enhance = compose(
    translate,
    connect(
        mapStateToProps,
        null,
    ),
    reduxForm(),
);

export default enhance(DiscountsEditForm);



// WEBPACK FOOTER //
// ./src/pages/discounts/edit/DiscountsEditForm.js